import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { AUTH_ROUTE } from "../../../../pages/App/routes";
import {
  useAppSelector,
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../../store/application/slice";
import {
  getAdminVacancyHistoryThunk
} from "../../../../store/vacancy/thunks";
import JobHeader from "../JobHeader";
import {
  DEFAULT_ITEMS_PER_PAGE,
  ITEMS_PER_PAGE_OPTIONS,
  MAX_VISIBLE_PAGES,
} from "../../../../constants/app";
import SelectInput from "../../../UI/Inputs/SelectInput";
import Pagination from "../../../Pagination/Pagination";
import HistoryCard from "../../../Client/Vacancies/History/HistoryCard";
import { HistoryVacancySortField, SortOrder, TVacancyDetail } from "../../../../store/vacancy/types";
import { STORAGE_KEYS } from "../../../../constants/storageKeys";
import storage from "../../../../utils/storage";
import { getCandidateDataForCentreThunk } from "../../../../store/centers/thunks";
import ViewProfile from "../../../Client/Candidates/PopupModal/ViewProfile";
import HistoryTableHeader from "../../../Client/Vacancies/History/HistoryTableHeader";

const HistoryVacancy: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const historyVacanciesData = useAppSelector(
    (state) => state.vacancy.historyVacancies
  );
  const [itemPerPageList, setItemPerPageList] = useState<number>(
    DEFAULT_ITEMS_PER_PAGE
  );
  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );

  const historyVacancyList = historyVacanciesData?.history_vacancies;

  type ModalType = "RATING" | "CANCEL" | null;
  const [sortBy, setSortBy] = useState<HistoryVacancySortField>("timeframe");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [historyDropdownId, setHistoryDropdownId] = useState<number | null>(
    null
  );
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [paginationLinks, setPaginationLinks] = useState<any[]>([]);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  
  const getScheduledVacanciesList = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getAdminVacancyHistoryThunk({
        page: currentPage,
        per_page: itemPerPageList,
        sort_order: sortOrder,
        sort_by: sortBy,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
      })
    );
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch, currentPage, itemPerPageList, sortBy, sortOrder, selectedCentreDetails]);

  useEffect(() => {
    getScheduledVacanciesList();
  }, [currentPage, itemPerPageList, getScheduledVacanciesList, sortBy]);

  /**
   * Handles the change in the number of items per page by updating the `itemPerPageList` state.
   *
   * @param value - The numeric value representing the number of items per page.
   */
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setCurrentPage(Number(page));
    }
  };

  /**
   * Updates the current page and items per page when the component mounts or when the `candidatesList` changes.
   */
  useEffect(() => {
    if (historyVacancyList) {
      setTotalPages(historyVacancyList?.last_page || 1);
      setPaginationLinks(historyVacancyList?.links);
    }
  }, [historyVacancyList]);

  const handlePageSizeChange = (value: string | number) => {
    setItemPerPageList(Number(value));
    setCurrentPage(1); // Reset to the first page when changing items per page
  };

  const handleSortChange = (field: HistoryVacancySortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };
  
  const handleRating = () => {
    setHistoryDropdownId(null);
    setActiveModal("RATING");
  };

  const handleViewVacancy = async (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_PENDING_VACANCIES);
  };

  const handleDuplicateVacancy = (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.ADMIN_CREATE_VACANCY);
  };

  const handleCandidateProfileView = async (vacancy: TVacancyDetail) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateDataForCentreThunk({
        candidate_id: String(vacancy.selected_candidate.uuid),
        centre_id: String(vacancy.centre_id),
      })
    );
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  const onClickClose = () => {
    setActiveModal(null);
    setShowProfileModal(false);
  };

  return (
    <>
      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />

      <div className="d-flex flex-column gap-20">
        <div className="d-flex flex-column">
          <JobHeader />
        </div>
        <div className="d-flex flex-column">
          {/* <div className="bg-white py-10px px-15px d-none d-lg-flex flex-column flex-lg-row gap-10 align-items-start align-items-lg-center justify-content-between border-bottom border-off-white fs-13 fw-400 text-black">
            <div className="flex-grow-1"></div>
            <div className="min-w-300px">
              <div
                className="fs-13 fw-700 d-flex gap-5 align-items-center cursor-pointer"
                onClick={() => handleTimeFrameSort("timeframe")}
              >
                Requested Timeframe
                <SortBy
                  fill={sortBy === "timeframe" ? "#167E76" : "#E0E0E0"}
                  sort_order={sortBy === "timeframe" ? sortOrder : ""}
                />
              </div>
            </div>
            <div
              className="min-w-120px d-flex gap-5 align-items-center cursor-pointer"
              onClick={() => handleTimeFrameSort("created_at")}
            >
              <div className="fs-13 fw-700 ">Posted</div>
              <SortBy
                fill={sortBy === "created_at" ? "#167E76" : "#E0E0E0"}
                sort_order={sortBy === "created_at" ? sortOrder : ""}
              />
            </div>
            <div className="min-w-200px">
              <div className="fs-13 fw-700">Settings</div>
            </div>
            <div className="min-w-165px"></div>
            <div className="min-w-24px"></div>
          </div> */}
          <HistoryTableHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
          />

          {historyVacancyList && historyVacancyList?.data?.length > 0 ? (
            historyVacancyList?.data?.map((item: any, index: number) => {
              return (
                <div key={index}>
                  {/* <VacancyCard vacancy={item} type="history" /> */}
                  <HistoryCard
                    key={item.id}
                    vacancy={item}
                    showDropdown={historyDropdownId === item.id}
                    setShowDropdown={(val) =>
                      setHistoryDropdownId(val ? item.id : null)
                    }
                    onViewDetails={() => handleViewVacancy(item)}
                    handleDuplicate={() => handleDuplicateVacancy(item)}
                    handleProfileView={() => handleCandidateProfileView(item)}
                    handlePreferred={() => {}}
                  />
                </div>
              );
            })
          ) : (
            <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
              <div className="fs-16 fw-400 text-grey-medium">
                No Vacancies Found
              </div>
            </div>
          )}

          {/* Pagination Component */}
          {historyVacanciesData &&
            historyVacanciesData.history_vacancies.total >
              DEFAULT_ITEMS_PER_PAGE && (
                <div className="pt-10px">
                  <div className="d-flex gap-5 align-items-center justify-content-end flex-wrap">
                    <div className="d-flex gap-3 align-items-center">
                      <div className="fs-13 text-black text-nowrap">
                        Items per page
                      </div>
                      <div className="form-group">
                        <SelectInput
                          options={ITEMS_PER_PAGE_OPTIONS.map((item) => ({
                            value: item,
                            label: item.toString(),
                          }))}
                          onChange={handlePageSizeChange}
                          className="form-select text-only"
                          value={itemPerPageList}
                        />
                      </div>
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      paginationLinks={paginationLinks}
                      maxVisiblePages={MAX_VISIBLE_PAGES}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
            )}
          {/* Pagination Component */}
        </div>
      </div>
    </>
  );
};

export default HistoryVacancy;
